import { useState, useEffect, useCallback } from 'react'
import { Plus, Upload, GripVertical, Edit2, Trash2, Save, X, Eye, EyeOff, ExternalLink, Image as ImageIcon } from 'lucide-react'
import { useAuth } from '../../../context/AuthContext'
import './TBTVChannels.css'

const HLS_STITCHER_BASE = 'https://play.tenband.tv'

const CHANNEL_TYPES = [
  { value: 'viloud-linear', label: '📡 Viloud Linear', mode: 'Linear' },
  { value: 'viloud-xmltv', label: '📺 Viloud XMLTV', mode: 'xmltv' },
  { value: 'tbtv-linear', label: '🎬 TBTV Linear', mode: 'tbtv-linear' },
]

function generateTbtvChannelId(existingChannels) {
  const existing = (existingChannels || [])
    .map(ch => ch.tbtv_channel_id)
    .filter(id => id && /^tbtv-ch-\d+$/.test(id))
    .map(id => parseInt(id.replace('tbtv-ch-', '')))
  const next = existing.length > 0 ? Math.max(...existing) + 1 : 1
  return `tbtv-ch-${String(next).padStart(3, '0')}`
}

export default function TBTVChannels() {
  const { api } = useAuth()
  const [channels, setChannels] = useState([])
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState('')
  const [editing, setEditing] = useState(null)
  const [editForm, setEditForm] = useState({})
  const [showAdd, setShowAdd] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const [dragItem, setDragItem] = useState(null)
  const [dragOver, setDragOver] = useState(null)

  const [newChannel, setNewChannel] = useState({
    viloud_id: '', name: '', mode: 'tbtv-linear', description: '',
    logo_url: '', channel_hls: '', cf_live_input_id: '', youtube_channel_id: '', vast_ad_tag: '',
    channel_type: 'tbtv-linear', tbtv_linear_playlist_id: ''
  })

  // TBTV Linear playlists (from Playlist Builder)
  const [linearPlaylists, setLinearPlaylists] = useState([])
  const [loadingPlaylists, setLoadingPlaylists] = useState(false)

  // Cloudflare Images picker (for channel logo)
  const [images, setImages] = useState([])
  const [loadingImages, setLoadingImages] = useState(false)
  const [imageSearch, setImageSearch] = useState('')
  const [showImagePicker, setShowImagePicker] = useState(false)
  const [imagePickerTarget, setImagePickerTarget] = useState('edit') // 'edit' | 'add'
  // Tag filter for the image picker
  const [imageTags, setImageTags] = useState([])
  const [activeImageTag, setActiveImageTag] = useState('')
  const [tagFilterIds, setTagFilterIds] = useState(null) // null = no tag filter

  const [csvText, setCsvText] = useState('')

  const loadChannels = useCallback(async () => {
    setLoading(true)
    try {
      const data = await api('/channels')
      setChannels(data.channels || [])
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setLoading(false)
  }, [api])

  useEffect(() => { loadChannels() }, [loadChannels])

  // Load tbtv-linear playlists when Add modal opens
  const loadLinearPlaylists = useCallback(async () => {
    setLoadingPlaylists(true)
    try {
      const res = await fetch(`${HLS_STITCHER_BASE}/api/channels`)
      const data = await res.json()
      // Only show Linear Channels (loop_playlist = 1)
      setLinearPlaylists((data.channels || []).filter(ch => ch.loop_playlist === 1))
    } catch (e) {
      console.error('Failed to load playlists:', e)
      setLinearPlaylists([])
    }
    setLoadingPlaylists(false)
  }, [])

  useEffect(() => {
    if (showAdd && newChannel.channel_type === 'tbtv-linear') {
      loadLinearPlaylists()
    }
  }, [showAdd, newChannel.channel_type, loadLinearPlaylists])

  // Load Cloudflare Images for the logo picker
  const loadImages = useCallback(async () => {
    setLoadingImages(true)
    try {
      const data = await api('/images?per_page=100&sort_order=desc')
      const result = data.result || data
      setImages(result.images || [])
    } catch (e) {
      console.error('Failed to load images:', e)
      setImages([])
    }
    setLoadingImages(false)
  }, [api])

  // Public delivery URL for a Cloudflare image (mirrors image-manager)
  function imagePublicUrl(img) {
    return img.variants?.find(v => v.endsWith('/public')) || img.variants?.[0] || ''
  }
  function imageThumbUrl(img) {
    return img.variants?.find(v => v.endsWith('/thumbnail')) || imagePublicUrl(img)
  }

  function openImagePicker(target = 'edit') {
    setImagePickerTarget(target)
    setShowImagePicker(true)
    if (images.length === 0) loadImages()
    if (imageTags.length === 0) {
      api('/tags').then(d => setImageTags(d.tags || [])).catch(() => {})
    }
  }

  // Resolve the active tag → set of image ids (client-side filter), mirroring image-manager
  useEffect(() => {
    if (!activeImageTag) { setTagFilterIds(null); return }
    api(`/tags/filter?tag=${encodeURIComponent(activeImageTag)}&type=image`)
      .then(d => setTagFilterIds(d.image_ids || []))
      .catch(() => setTagFilterIds([]))
  }, [activeImageTag, api])
  function selectImage(img) {
    const url = imagePublicUrl(img)
    if (imagePickerTarget === 'add') {
      setNewChannel(prev => ({ ...prev, logo_url: url }))
    } else {
      setEditForm(prev => ({ ...prev, logo_url: url }))
    }
    setShowImagePicker(false)
  }

  // Handle channel type change in Add modal
  function handleTypeChange(channelType) {
    const typeInfo = CHANNEL_TYPES.find(t => t.value === channelType)
    setNewChannel(prev => ({
      ...prev,
      channel_type: channelType,
      mode: typeInfo?.mode || 'Linear',
      // Clear type-specific fields
      viloud_id: '',
      channel_hls: '',
      tbtv_linear_playlist_id: '',
    }))
    if (channelType === 'tbtv-linear') loadLinearPlaylists()
  }

  // Handle playlist selection for tbtv-linear
  function handlePlaylistSelect(playlistId) {
    const playlist = linearPlaylists.find(p => p.id === playlistId)
    if (!playlist) return
    setNewChannel(prev => ({
      ...prev,
      tbtv_linear_playlist_id: playlistId,
      viloud_id: `tbtv-${playlistId}`,
      name: playlist.name,
      channel_hls: `${HLS_STITCHER_BASE}/channel/${playlistId}/stream.m3u8`,
    }))
  }

  // Drag and drop reorder
  function handleDragStart(idx) { setDragItem(idx) }
  function handleDragEnter(idx) { setDragOver(idx) }
  function handleDragEnd() {
    if (dragItem === null || dragOver === null || dragItem === dragOver) {
      setDragItem(null); setDragOver(null); return
    }
    const reordered = [...channels]
    const [moved] = reordered.splice(dragItem, 1)
    reordered.splice(dragOver, 0, moved)
    setChannels(reordered)
    setDragItem(null); setDragOver(null)
    saveOrder(reordered)
  }

  async function saveOrder(list) {
    try {
      await api('/channels/reorder', {
        method: 'POST',
        body: JSON.stringify({ order: list.map(c => c.viloud_id) })
      })
      setStatus('✅ Order saved')
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  async function handleAdd() {
    if (!newChannel.viloud_id || !newChannel.name) {
      setStatus('Channel ID and name are required'); return
    }
    try {
      const payload = {
        ...newChannel,
        tbtv_channel_id: generateTbtvChannelId(channels),
      }
      await api('/channels', { method: 'POST', body: JSON.stringify(payload) })
      setStatus('✅ Channel added')
      setShowAdd(false)
      setNewChannel({
        viloud_id: '', name: '', mode: 'tbtv-linear', description: '',
        logo_url: '', channel_hls: '', cf_live_input_id: '', youtube_channel_id: '', vast_ad_tag: '',
        channel_type: 'tbtv-linear', tbtv_linear_playlist_id: ''
      })
      loadChannels()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  function startEdit(ch) {
    setEditing(ch.viloud_id)
    setEditForm({ ...ch })
    // Load playlists so tbtv-linear channels can swap playlist (keeps EPG id + HLS in sync)
    if (ch.channel_type === 'tbtv-linear' || ch.mode === 'tbtv-linear') loadLinearPlaylists()
  }

  // Handle playlist selection in the Edit modal — updates BOTH the EPG playlist id
  // (tbtv_linear_playlist_id, used to build the guide) and the video HLS URL together,
  // so they can never drift apart. Mirrors handlePlaylistSelect for the Add modal.
  function handleEditPlaylistSelect(playlistId) {
    setEditForm(prev => ({
      ...prev,
      tbtv_linear_playlist_id: playlistId,
      channel_hls: `${HLS_STITCHER_BASE}/channel/${playlistId}/stream.m3u8`,
    }))
  }

  async function handleSaveEdit() {
    try {
      await api(`/channels/${editing}`, { method: 'PATCH', body: JSON.stringify(editForm) })
      setStatus('✅ Updated')
      setEditing(null)
      loadChannels()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  async function handleDelete(viloudId, name) {
    if (!confirm(`Delete channel "${name}"?`)) return
    try {
      await api(`/channels/${viloudId}`, { method: 'DELETE' })
      setStatus('✅ Deleted')
      loadChannels()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  async function handleToggleActive(ch) {
    try {
      await api(`/channels/${ch.viloud_id}`, {
        method: 'PATCH',
        body: JSON.stringify({ is_active: !ch.is_active })
      })
      loadChannels()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  // CSV Import
  function parseCSV(text) {
    const lines = text.split('\n').filter(l => l.trim())
    if (lines.length < 2) return null
    const headers = parseCSVLine(lines[0])
    const rows = []
    for (let i = 1; i < lines.length; i++) {
      const vals = parseCSVLine(lines[i])
      const row = {}
      headers.forEach((h, idx) => { row[h.trim()] = (vals[idx] || '').trim() })
      if (row.channel_id || row.viloud_id) rows.push(row)
    }
    return rows
  }

  function parseCSVLine(line) {
    const result = []; let current = ''; let inQuotes = false
    for (let i = 0; i < line.length; i++) {
      const ch = line[i]
      if (ch === '"') inQuotes = !inQuotes
      else if (ch === ',' && !inQuotes) { result.push(current); current = '' }
      else current += ch
    }
    result.push(current); return result
  }

  async function handleImportCSV() {
    const rows = parseCSV(csvText)
    if (!rows || !rows.length) { setStatus('No valid rows found'); return }
    try {
      const data = await api('/channels/import-csv', {
        method: 'POST', body: JSON.stringify({ rows })
      })
      setStatus(`✅ Imported ${data.imported} channels`)
      setShowImport(false); setCsvText('')
      loadChannels()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  function handleFile(e) {
    const file = e.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => setCsvText(ev.target.result)
    reader.readAsText(file)
  }

  function getTypeBadge(ch) {
    const ct = ch.channel_type || (ch.mode === 'xmltv' ? 'viloud-xmltv' : ch.mode === 'tbtv-linear' ? 'tbtv-linear' : 'viloud-linear')
    if (ct === 'tbtv-linear') return <span className="tbtv-type-badge tbtv-linear-badge">TBTV</span>
    if (ct === 'viloud-xmltv') return <span className="tbtv-type-badge viloud-xmltv-badge">XMLTV</span>
    return <span className="tbtv-type-badge viloud-linear-badge">Viloud</span>
  }

  const isTbtvLinear = newChannel.channel_type === 'tbtv-linear'

  // Images visible in the picker after tag filter + search
  const pickerImages = images
    .filter(img => tagFilterIds === null || tagFilterIds.includes(img.id))
    .filter(img => {
      if (!imageSearch) return true
      const q = imageSearch.toLowerCase()
      return (img.filename || '').toLowerCase().includes(q) || (img.id || '').toLowerCase().includes(q)
    })

  return (
    <div className="tbtv-channels">
      <div className="tbtv-header">
        <div className="tbtv-header-left">
          <h1>TBTV Channels</h1>
          <span className="tbtv-count">{channels.length} channels</span>
        </div>
        <div className="tbtv-header-actions">
          <button className="btn-ghost" onClick={() => setShowImport(true)}>
            <Upload size={14} /> Import CSV
          </button>
          <button className="btn-primary" onClick={() => setShowAdd(true)}>
            <Plus size={14} /> Add Channel
          </button>
        </div>
      </div>

      {status && (
        <div className={`tbtv-status ${status.startsWith('Error') ? 'error' : ''}`}>
          {status}
          <button className="btn-ghost btn-sm" onClick={() => setStatus('')}>✕</button>
        </div>
      )}

      {loading ? <p className="muted">Loading...</p> : (
        <div className="tbtv-list">
          {channels.map((ch, idx) => (
            <div
              key={ch.viloud_id}
              className={`tbtv-item ${!ch.is_active ? 'inactive' : ''} ${dragOver === idx ? 'drag-over' : ''}`}
              draggable
              onDragStart={() => handleDragStart(idx)}
              onDragEnter={() => handleDragEnter(idx)}
              onDragEnd={handleDragEnd}
              onDragOver={e => e.preventDefault()}
            >
              {editing === ch.viloud_id ? (
                <div className="tbtv-edit-form">
                  <div className="tbtv-edit-grid">
                    <div className="form-row"><label>Name</label>
                      <input value={editForm.name || ''} onChange={e => setEditForm({...editForm, name: e.target.value})} /></div>
                    <div className="form-row"><label>Mode</label>
                      <select value={editForm.mode || 'Linear'} onChange={e => setEditForm({...editForm, mode: e.target.value})}>
                        <option>Linear</option><option>xmltv</option><option>tbtv-linear</option>
                      </select></div>
                    <div className="form-row full-width"><label>Description</label>
                      <textarea value={editForm.description || ''} onChange={e => setEditForm({...editForm, description: e.target.value})} rows={2} /></div>
                    {(editForm.channel_type === 'tbtv-linear' || editForm.mode === 'tbtv-linear') ? (
                      <div className="form-row full-width"><label>Logo Image</label>
                        <div className="tbtv-image-picker-control">
                          {editForm.logo_url
                            ? <img className="tbtv-logo-preview" src={editForm.logo_url} alt="" />
                            : <div className="tbtv-logo-preview tbtv-logo-empty">No image</div>}
                          <button type="button" className="btn-ghost btn-sm" onClick={() => openImagePicker('edit')}>
                            <ImageIcon size={12} /> Choose
                          </button>
                          <input className="tbtv-logo-url-input" value={editForm.logo_url || ''} onChange={e => setEditForm({...editForm, logo_url: e.target.value})} placeholder="Pick an image or paste a URL" />
                        </div></div>
                    ) : (
                      <div className="form-row"><label>Logo URL</label>
                        <input value={editForm.logo_url || ''} onChange={e => setEditForm({...editForm, logo_url: e.target.value})} /></div>
                    )}
                    {(editForm.channel_type === 'tbtv-linear' || editForm.mode === 'tbtv-linear') ? (
                      <>
                        <div className="form-row"><label>Linear Channel (EPG + video)</label>
                          <select
                            value={editForm.tbtv_linear_playlist_id || ''}
                            onChange={e => handleEditPlaylistSelect(e.target.value)}
                            disabled={loadingPlaylists}
                          >
                            <option value="" disabled>{loadingPlaylists ? 'Loading playlists…' : 'Select a linear channel'}</option>
                            {editForm.tbtv_linear_playlist_id && !linearPlaylists.some(p => p.id === editForm.tbtv_linear_playlist_id) && (
                              <option value={editForm.tbtv_linear_playlist_id}>{editForm.tbtv_linear_playlist_id}</option>
                            )}
                            {linearPlaylists.map(p => (
                              <option key={p.id} value={p.id}>{p.name} ({p.id})</option>
                            ))}
                          </select></div>
                        <div className="form-row"><label>Channel HLS (auto)</label>
                          <input value={editForm.channel_hls || ''} readOnly title="Set automatically from the selected linear channel" /></div>
                      </>
                    ) : (
                      <div className="form-row"><label>Channel HLS</label>
                        <input value={editForm.channel_hls || ''} onChange={e => setEditForm({...editForm, channel_hls: e.target.value})} /></div>
                    )}
                    <div className="form-row"><label>CF Live Input ID</label>
                      <input value={editForm.cf_live_input_id || ''} onChange={e => setEditForm({...editForm, cf_live_input_id: e.target.value})} /></div>
                    <div className="form-row"><label>VAST Ad Tag</label>
                      <input value={editForm.vast_ad_tag || ''} onChange={e => setEditForm({...editForm, vast_ad_tag: e.target.value})} /></div>
                  </div>
                  <div className="tbtv-edit-actions">
                    <button className="btn-primary" onClick={handleSaveEdit}><Save size={12} /> Save</button>
                    <button className="btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="tbtv-drag"><GripVertical size={16} /></div>
                  <div className="tbtv-order">{idx + 1}</div>
                  {ch.logo_url && <img className="tbtv-logo" src={ch.logo_url} alt="" />}
                  <div className="tbtv-info">
                    <div className="tbtv-name">{ch.name}</div>
                    <div className="tbtv-meta">
                      {getTypeBadge(ch)}
                      <span className="tbtv-channel-id" title="TBTV Channel ID">{ch.tbtv_channel_id || '—'}</span>
                      {ch.cf_live_input_id && <span title="CF Live Input">📡 CF</span>}
                    </div>
                  </div>
                  <div className="tbtv-actions">
                    <button className="btn-ghost btn-sm" onClick={() => handleToggleActive(ch)} title={ch.is_active ? 'Active' : 'Hidden'}>
                      {ch.is_active ? <Eye size={14} /> : <EyeOff size={14} />}
                    </button>
                    <button className="btn-ghost btn-sm" onClick={() => startEdit(ch)} title="Edit">
                      <Edit2 size={14} />
                    </button>
                    <button className="btn-ghost btn-sm" onClick={() => handleDelete(ch.viloud_id, ch.name)} title="Delete">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add Channel Modal */}
      {showAdd && (
        <div className="modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="tbtv-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add TBTV Channel</h2>
              <button className="btn-ghost" onClick={() => setShowAdd(false)}><X size={16} /></button>
            </div>

            {/* Channel Type Selector */}
            <div className="tbtv-type-selector">
              {CHANNEL_TYPES.map(t => (
                <button
                  key={t.value}
                  className={`tbtv-type-btn ${newChannel.channel_type === t.value ? 'active' : ''}`}
                  onClick={() => handleTypeChange(t.value)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="tbtv-add-grid">
              {/* TBTV Linear: Playlist Picker (left-justified select, matches edit modal) */}
              {isTbtvLinear ? (
                <>
                  <div className="form-row"><label>Linear Channel (EPG + video)</label>
                    <select
                      value={newChannel.tbtv_linear_playlist_id || ''}
                      onChange={e => handlePlaylistSelect(e.target.value)}
                      disabled={loadingPlaylists}
                    >
                      <option value="" disabled>{loadingPlaylists ? 'Loading playlists…' : 'Select a linear channel'}</option>
                      {linearPlaylists.map(p => (
                        <option key={p.id} value={p.id}>{p.name} ({p.id})</option>
                      ))}
                    </select></div>
                  <div className="form-row"><label>Channel HLS (auto)</label>
                    <input value={newChannel.channel_hls || ''} readOnly title="Set automatically from the selected linear channel" /></div>
                </>
              ) : (
                /* Viloud types: manual ID entry */
                <div className="form-row">
                  <label>Viloud Channel ID *</label>
                  <input
                    value={newChannel.viloud_id}
                    onChange={e => setNewChannel({...newChannel, viloud_id: e.target.value})}
                    placeholder="a68a72d5abdb040c7b279c707ed5477e"
                  />
                </div>
              )}

              <div className="form-row"><label>Name *</label>
                <input value={newChannel.name} onChange={e => setNewChannel({...newChannel, name: e.target.value})} placeholder="Channel Name" /></div>

              <div className="form-row full-width"><label>Description</label>
                <textarea value={newChannel.description} onChange={e => setNewChannel({...newChannel, description: e.target.value})} rows={2} /></div>

              {isTbtvLinear ? (
                <div className="form-row full-width"><label>Logo Image</label>
                  <div className="tbtv-image-picker-control">
                    {newChannel.logo_url
                      ? <img className="tbtv-logo-preview" src={newChannel.logo_url} alt="" />
                      : <div className="tbtv-logo-preview tbtv-logo-empty">No image</div>}
                    <button type="button" className="btn-ghost btn-sm" onClick={() => openImagePicker('add')}>
                      <ImageIcon size={12} /> Choose
                    </button>
                    <input className="tbtv-logo-url-input" value={newChannel.logo_url} onChange={e => setNewChannel({...newChannel, logo_url: e.target.value})} placeholder="Pick an image or paste a URL" />
                  </div></div>
              ) : (
                <div className="form-row"><label>Logo URL</label>
                  <input value={newChannel.logo_url} onChange={e => setNewChannel({...newChannel, logo_url: e.target.value})} /></div>
              )}

              {!isTbtvLinear && (
                <div className="form-row"><label>Channel HLS</label>
                  <input value={newChannel.channel_hls} onChange={e => setNewChannel({...newChannel, channel_hls: e.target.value})}
                    placeholder="https://deliver.viloud.tv/..." /></div>
              )}

              <div className="form-row"><label>CF Live Input ID</label>
                <input value={newChannel.cf_live_input_id} onChange={e => setNewChannel({...newChannel, cf_live_input_id: e.target.value})} /></div>

              <div className="form-row"><label>VAST Ad Tag</label>
                <input value={newChannel.vast_ad_tag} onChange={e => setNewChannel({...newChannel, vast_ad_tag: e.target.value})} /></div>
            </div>

            <div className="tbtv-add-actions">
              <button className="btn-primary" onClick={handleAdd} disabled={!newChannel.viloud_id || !newChannel.name}>
                <Plus size={14} /> Add Channel
              </button>
              <button className="btn-ghost" onClick={() => setShowAdd(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Import CSV Modal */}
      {showImport && (
        <div className="modal-overlay" onClick={() => setShowImport(false)}>
          <div className="tbtv-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Upload size={16} /> Import Channels from CSV</h2>
              <button className="btn-ghost" onClick={() => setShowImport(false)}><X size={16} /></button>
            </div>
            <p className="muted">Paste your ChannelGuide CSV or upload the file. Expected columns: channel_id, name, mode, description, logo_url, channel_hls</p>
            <div className="yt-import-file">
              <label className="btn-ghost">
                Choose CSV File
                <input type="file" accept=".csv" onChange={handleFile} style={{ display: 'none' }} />
              </label>
            </div>
            <textarea
              className="yt-import-textarea"
              value={csvText}
              onChange={e => setCsvText(e.target.value)}
              placeholder="Paste CSV here..."
              rows={8}
            />
            <div className="tbtv-add-actions">
              <button className="btn-primary" onClick={handleImportCSV} disabled={!csvText.trim()}>
                <Upload size={14} /> Import
              </button>
              <button className="btn-ghost" onClick={() => setShowImport(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Cloudflare Images Picker */}
      {showImagePicker && (
        <div className="modal-overlay" onClick={() => setShowImagePicker(false)}>
          <div className="tbtv-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><ImageIcon size={16} /> Choose Logo Image</h2>
              <button className="btn-ghost" onClick={() => setShowImagePicker(false)}><X size={16} /></button>
            </div>
            <div className="tbtv-image-filters">
              <input
                className="tbtv-image-search"
                placeholder="Search images by name or ID…"
                value={imageSearch}
                onChange={e => setImageSearch(e.target.value)}
              />
              <select
                className="tbtv-image-tag-filter"
                value={activeImageTag}
                onChange={e => setActiveImageTag(e.target.value)}
                title="Filter by tag"
              >
                <option value="">All tags</option>
                {imageTags.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            {loadingImages ? (
              <div className="tbtv-picker-loading">Loading images…</div>
            ) : images.length === 0 ? (
              <div className="tbtv-picker-loading">No Cloudflare Images found</div>
            ) : pickerImages.length === 0 ? (
              <div className="tbtv-picker-loading">
                {activeImageTag ? `No images tagged "${activeImageTag}"` : 'No images match your search'}
              </div>
            ) : (
              <div className="tbtv-image-grid">
                {pickerImages.map(img => {
                  const url = imagePublicUrl(img)
                  const currentLogo = imagePickerTarget === 'add' ? newChannel.logo_url : editForm.logo_url
                  return (
                    <div
                      key={img.id}
                      className={`tbtv-image-tile ${currentLogo === url ? 'selected' : ''}`}
                      onClick={() => selectImage(img)}
                      title={img.filename || img.id}
                    >
                      <img src={imageThumbUrl(img)} alt={img.filename || img.id} loading="lazy" />
                      <div className="tbtv-image-name">{img.filename || img.id}</div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
